import React, { useCallback } from 'react';
import { useFormikContext } from 'formik';
import { TextInput2Props, TextInput2 } from '@el8/vital';
import { MergeWithPriority } from 'types';

export type TextInputFormikProps = MergeWithPriority<
  {
    /**
     * A regex that describes allowed values of the input. If a change event is fired
     * and the incoming value fails the regex test, the change handler will NOT be
     * called (the event will effectively be a no-op).
     *
     * **Note**: This test will not run if the incoming value is an empty string. Use form
     * validation to indicate that empty fields are not allowed.
     */
    allowedRegex?: RegExp;

    name: TextInput2Props['name'];

    /**
     * OPTIONAL change handler if you want to do something in addition to updating
     * form values when the input value changes. You don't need this prop to handle
     * value updates; this component does that automatically.
     */
    onChange?: TextInput2Props['onChange'];

    /**
     * By default, error messages will be displayed if there are errors for this field. If
     * this prop is `false`, then only error outlines will be shown to indicate an error state,
     * and error messages will not be shown.
     */
    showFullErrors?: boolean;

    /**
     * A function that, if provided, will be called inside onChange before anything
     * else happens, to transform the incoming new value. This can be used to automatically
     * format the value, for example when using phone number inputs.
     *
     * @returns the transformed value
     */
    transformValue?: (newValue: string) => string;

    type?: Exclude<TextInput2Props['type'], 'number' | 'date' | 'time'>;

    /**
     * Generally not needed unless you want to override the default behavior of
     * this component, which is to automatically pass in the value associated with
     * `name` to the underlying input using Formik context.
     */
    value?: string;
  },
  TextInput2Props
>;

/**
 * A Formik-integrated version of Vital's TextInput2. 'error', 'onBlur', `onChange` and
 * `value` are automatically wired up based on the `name` prop passed in.
 */
const TextInputFormik: React.FC<TextInputFormikProps> = ({
  allowedRegex,
  disabled = false,
  name,
  onBlur,
  onChange,
  showFullErrors = true,
  transformValue,
  ...props
}) => {
  const {
    errors,
    handleBlur: formikBlur,
    handleChange: formikChange,
    isSubmitting,
    touched,
    values,
  } = useFormikContext<{ [field: string]: string }>();

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>): void => {
      formikBlur(event);
      if (onBlur) {
        onBlur(event);
      }
    },
    [formikBlur, onBlur],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (transformValue) {
        // eslint-disable-next-line no-param-reassign
        event.target.value = transformValue(event.target.value);
      }

      if (allowedRegex && event.target.value && !allowedRegex.test(event.target.value)) return;

      formikChange(event);
      if (onChange) {
        onChange(event);
      }
    },
    [allowedRegex, formikChange, onChange, transformValue],
  );

  return (
    <TextInput2
      disabled={isSubmitting || disabled}
      error={touched[name] && (showFullErrors ? errors[name] : Boolean(errors[name]))}
      name={name}
      onBlur={handleBlur}
      onChange={handleChange}
      value={values[name]}
      {...props}
    />
  );
};

export default TextInputFormik;
